<template>
  <UiBaseFormVee
    v-if="userCopy"
    :errorText="errorText"
    submitOnCtrlEnter
    :onSubmit="onSubmit">
    <UiInputTextFieldVee
      :name="'firstName'"
      :label="$t('form.label.first-name')"
      :rules="['required']"
      isRequired
      :isDisabled="!canUpdateUser"
      v-model="userCopy.firstName" />
    <UiInputTextFieldVee
      :name="'lastName'"
      :label="$t('form.label.last-name')"
      :rules="['required']"
      isRequired
      :isDisabled="!canUpdateUser"
      v-model="userCopy.lastName" />
    <UiInputTextFieldVee
      :name="'email'"
      :label="$t('form.label.email')"
      isDisabled
      v-model="userCopy.email" />
    <UiInputPhoneVee
      :name="'phone'"
      :label="$t('form.label.phone')"
      :rules="['requiredPhone', 'phone']"
      :isDisabled="!canUpdateUser"
      v-model="userCopy.phone" />
    <div class="mb-3">
      <span class="mb-1 block text-sm font-semibold">{{ $t('W: Sprog') }}</span>
      <UiLangSelect
        v-model="userCopy.languageCode"
        :options="languageOptions"
        :ariaLabel="$t('W: Sprog')"
        :isDisabled="!canUpdateUser" />
    </div>

    <UiComboboxVee
      v-if="showLocationSection"
      name="assignedLocationID"
      :label="$t('W: Lokation')"
      :placeholder="$t('W: Ingen valgt')"
      :options="locationOptions"
      :isDisabled="!canUpdateUser"
      v-model="assignedLocationIDProxy" />

    <ImageUpload
      v-if="canEditSignature"
      :title="$t('W: Underskrift')"
      :image="userCopy.images.signature"
      :allowedImageTypes="['image/png', 'image/jpeg', 'image/webp', 'image/avif']"
      :maxDim="800"
      :uploadPath="user.signatureUploadPath()"
      fileNameFormat="signature-%s"
      @image-selected="userCopy.images.signature = $event"
      @image-removed="userCopy.images.signature = null" />

    <template
      #buttons
      v-if="canUpdateUser">
      <template v-if="canUpdateCriticals">
        <UiBaseButton
          v-if="userCopy.activeState === 'active'"
          theme="neutral"
          :text="$t('action.deactivate')"
          :isLoading="isUpdatingStatus"
          :isDisabled="isSaving"
          @button-click="onDisableUserClick" />
        <UiBaseButton
          v-else
          theme="neutral"
          :text="$t('action.activate')"
          :isLoading="isUpdatingStatus"
          :isDisabled="isSaving"
          @button-click="onEnableUserClick" />
      </template>
      <UiBaseButton
        :text="$t('action.save')"
        :isLoading="isSaving"
        :isDisabled="isUpdatingStatus"
        type="submit" />
    </template>
  </UiBaseFormVee>
</template>

<script lang="ts">
import type { LabManagementUser } from '@/globals/typescript/models/LabManagementUser'
import { getRoleDisplayName } from '@/globals/typescript/utils/roleUtils'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useToast } from '@/globals/typescript/utils/toast'
import { useModalStore } from '@/stores/modalStore'
import { useUserStore } from '@/stores/userStore'
import { useAccountStore } from '@/stores/accountStore'
import { useImageStore } from '@/stores/imageStore/imageStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { i18n } from '@/locales'
import type { TLabManagementRole } from '@team-milva/lab-models/user'
import type { ILangSelectOption } from '@team-milva/lab-components'
import {
  LAB_MANAGEMENT_LANGUAGES,
  LOCALE_COUNTRY_CODES,
  LOCALE_NATIVE_NAMES,
  type TLabManagementLocale,
} from '@team-milva/lab-models/language'
import { cloneDeep, isEqual, omit } from 'lodash-es'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'UserInfoForm',
  props: {
    user: {
      type: Object as PropType<LabManagementUser>,
      required: true,
    },
  },
  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      userCopy: null as LabManagementUser | null,

      // States
      isSaving: false,
      isUpdatingStatus: false,

      // Texts
      errorText: '',
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser', 'isLabAdminUser']),
    ...mapState(useAccountStore, ['availableLanguages']),
    ...mapState(useLabLocationStore, ['labLocations', 'activeLabLocations']),
    showLocationSection(): boolean {
      if (this.labLocations.length === 0) return false
      if (this.activeLabLocations.length > 0) return true
      return !!this.userCopy?.assignedLocationID
    },
    locationOptions(): { value: string; text: string }[] {
      const options = this.activeLabLocations
        .map((labLocation) => ({ value: labLocation.id, text: labLocation.name }))
        .sort((a, b) => a.text.localeCompare(b.text))

      const currentID = this.userCopy?.assignedLocationID
      const currentIsInactive =
        currentID && !this.activeLabLocations.some((x) => x.id === currentID)
      if (currentIsInactive) {
        const inactiveLocation = this.labLocations.find((x) => x.id === currentID)
        if (inactiveLocation) {
          options.push({ value: inactiveLocation.id, text: inactiveLocation.name })
        }
      }

      return [{ value: '', text: this.$t('W: Ingen valgt') }, ...options]
    },
    assignedLocationIDProxy: {
      get(): string {
        return this.userCopy?.assignedLocationID ?? ''
      },
      set(value: string) {
        if (this.userCopy) {
          this.userCopy.assignedLocationID = value || null
        }
      },
    },
    languageOptions(): ILangSelectOption[] {
      return this.availableLanguages.map((lang) => ({
        value: lang.code,
        code: lang.code,
        text: LOCALE_NATIVE_NAMES[lang.locale as TLabManagementLocale] ?? lang.name,
        countryCode: LOCALE_COUNTRY_CODES[lang.locale as TLabManagementLocale] ?? lang.locale,
      }))
    },
    title(): string {
      return this.user?.fullName || ''
    },
    isOwnUser(): boolean {
      return this.user?.id === this.currentUser?.id
    },
    canUpdateUser() {
      if (this.isOwnUser) {
        return true
      }

      if (this.isLabAdminUser) {
        return true
      }

      if (this.user.mainRole === 'owner') {
        return false
      }

      return true
    },
    // Signature editing is Owner + self only — a self-exception on top of
    // employees.signature, matching the Owner self-edit-work-areas exception.
    canEditSignature(): boolean {
      if (this.isOwnUser) {
        return true
      }

      return this.canUser('employees.signature')
    },
    canUpdateCriticals(): boolean {
      if (!this.canUpdateUser) {
        return false
      }

      if (this.user?.id === this.currentUser?.id) {
        return false
      }

      if (this.isLabAdminUser) {
        return true
      }

      if (this.user.mainRole === 'owner') {
        return false
      }

      if (!this.canUser('employees.manage')) {
        return false
      }

      return true
    },
    allowedRoles(): TLabManagementRole[] {
      return this.currentUser?.getAllowedRolesToManage() ?? []
    },
    roleOptions(): Array<{ value: TLabManagementRole; text: string }> {
      return this.allowedRoles.map((role) => {
        return {
          value: role,
          text: getRoleDisplayName(role),
        }
      })
    },
    userRoleText(): string {
      return getRoleDisplayName(this.userCopy?.mainRole ?? 'none')
    },
  },
  watch: {
    user: {
      handler() {
        this.setDataOnLoad()
      },
      immediate: true,
    },
    userCopy: {
      handler() {
        this.errorText = ''
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useImageStore, ['uploadImages']),
    setDataOnLoad() {
      this.userCopy = cloneDeep(this.user)
      this.unsavedGuard.takeSnapshot()
    },
    async onSubmit() {
      if (!this.userCopy) {
        this.errorText = this.$t('error.common.standard')
        return
      }

      this.isSaving = true

      try {
        const signatureChanged = !isEqual(
          this.userCopy.images.signature,
          this.user.images.signature
        )

        if (signatureChanged && this.userCopy.images.signature) {
          await this.uploadImages({ images: [this.userCopy.images.signature] })
        }

        const signature = this.userCopy.images.signature
        const languageChanged = this.userCopy.languageCode !== this.user.languageCode
        const userValues = {
          firstName: this.userCopy.firstName,
          lastName: this.userCopy.lastName,
          phone: this.userCopy.phone,
          mainRole: this.userCopy.mainRole,
          languageCode: this.userCopy.languageCode,
          assignedLocationID: this.userCopy.assignedLocationID,
          images: {
            signature: signature
              ? omit(signature, ['base64URL', 'svgFile'])
              : null,
          },
        }

        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)('patch', '/users/{userID}', {
          path: {
            userID: this.user.id,
          },
          body: userValues,
        })

        if (result.success) {
          if (languageChanged && this.user.id === this.currentUser?.id) {
            const locale = LAB_MANAGEMENT_LANGUAGES[this.userCopy.languageCode]?.locale
            if (locale) {
              i18n.global.locale.value = locale as TLabManagementLocale
            }
          }
          this.unsavedGuard.takeSnapshot()
          useToast().success(this.$t('W: Medarbejder gemt'))
          return
        }

        useToast().error(this.$t('W: Kunne ikke gemme medarbejder'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isSaving = false
      }
    },
    onDisableUserClick() {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        props: {
          title: this.$t('message.are-you-sure'),
          textLines: [this.$t('area.employee.info.message.deactivate')],
          onConfirm: () => {
            this.updateUserStatus({ activeState: 'inactive' })
          },
        },
      })
    },
    onEnableUserClick() {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        props: {
          title: this.$t('message.are-you-sure'),
          textLines: [this.$t('area.employee.info.message.activate')],
          onConfirm: () => {
            this.updateUserStatus({ activeState: 'active' })
          },
        },
      })
    },
    async updateUserStatus({
      activeState,
    }: {
      activeState: 'active' | 'inactive'
    }) {
      // Validations
      if (!this.user || !this.userCopy) {
        this.errorText = this.$t('error.common.standard')
        return
      }

      this.isUpdatingStatus = true

      try {
        const result = await managementApi('patch', '/users/{userID}/status', {
          path: {
            userID: this.user.id,
          },
          body: {
            activeState,
          },
        })

        if (result.success) {
          const { success } = useToast()
          if (activeState === 'inactive') {
            success(this.$t('W: Medarbejder deaktiveret'))
          } else {
            success(this.$t('W: Medarbejder aktiveret'))
          }
          return
        }

        useToast().error(
          this.$t('W: Kunne ikke opdatere medarbejderens status')
        )
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isUpdatingStatus = false
      }
    },
  },

  created() {
    // The `user` watcher runs immediate, so userCopy is already cloned by the
    // time this registers and takes its first baseline.
    this.unsavedGuard.register({
      snapshot: () => this.userCopy,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

